import React from 'react';
import styled from 'styled-components';
import { Video } from '../types'; // Corrección de la importación
import { FiX } from 'react-icons/fi';
import { tokens } from '../design/tokens';
import { useVideoForm } from '../hooks/useVideoForm';

interface VideoFormProps {
  video: Video | null;
  onClose: () => void;
}

// Contenedor modal
const ModalContainer = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${tokens.spacing.md};
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${tokens.spacing.xl};
  border-bottom: 1px solid #e9ecef;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #212529;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6c757d;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
`;

const Form = styled.form`
  padding: ${tokens.spacing.xl};
`;

const FormGroup = styled.div`
  margin-bottom: ${tokens.spacing.xl};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${tokens.spacing.sm};
  font-weight: 500;
  color: #495057;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: ${tokens.spacing.md};
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: #fff;

  &:focus {
    border-color: ${tokens.colors.primary};
    box-shadow: 0 0 0 3px rgba(183, 45, 164, 0.1);
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: ${tokens.spacing.md};
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: #fff;

  &:focus {
    border-color: ${tokens.colors.primary};
    box-shadow: 0 0 0 3px rgba(183, 45, 164, 0.1);
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const ThumbnailContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${tokens.spacing.md};
`;

const Thumbnail = styled.img`
  width: 120px;
  height: 90px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${tokens.spacing.md};
  justify-content: flex-end;
  margin-top: ${tokens.spacing.xl};
  padding-top: ${tokens.spacing.xl};
  border-top: 1px solid #e9ecef;
`;

const Button = styled.button<{ $primary?: boolean }>`
  background: ${props => props.$primary ? tokens.colors.primary : '#6c757d'};
  color: white;
  border: none;
  padding: ${tokens.spacing.md} ${tokens.spacing.xl};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$primary ? '#a0268f' : '#5a6268'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 12px;
  margin-top: ${tokens.spacing.xs};
  display: block;
`;

const LoadingText = styled.div`
  color: #6c757d;
  font-style: italic;
  margin-top: ${tokens.spacing.sm};
`;

const VideoForm: React.FC<VideoFormProps> = ({ video, onClose }) => {
  const {
    form: { register, handleSubmit, formState: { errors } },
    levels,
    loadingMeta,
    thumb,
    fetchMetadata,
    onSubmit
  } = useVideoForm({ video, onClose });

  return (
    <ModalContainer onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{video ? 'Editar vídeo' : 'Agregar vídeo'}</ModalTitle>
          <CloseButton onClick={onClose}>
            {React.createElement(FiX as any, { size: 20 })}
          </CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label>URL del vídeo *</Label>
            <Input
              {...register('url', {
                required: 'La URL es obligatoria',
                pattern: {
                  value: /^(https?:\/\/)?((www\.)?youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)[\w-]+/i,
                  message: 'URL de YouTube inválida'
                }
              })}
              placeholder="https://www.youtube.com/watch?v=..."
              onBlur={e => fetchMetadata(e.target.value)}
            />
            {errors.url && <ErrorMessage>{errors.url.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Nivel *</Label>
            <Input
              {...register('level', { required: 'El nivel es obligatorio' })}
              list="levels"
              placeholder="Seleccione o escriba un nuevo nivel"
            />
            <datalist id="levels">
              {levels.map(l => <option key={l} value={l} />)}
            </datalist>
            {errors.level && <ErrorMessage>{errors.level.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Título</Label>
            <Input
              {...register('title')}
              placeholder="Título auto-completado de YouTube"
            />
          </FormGroup>

          <FormGroup>
            <Label>Descripción</Label>
            <Textarea
              {...register('description')}
              placeholder="Descripción auto-completada de YouTube"
            />
          </FormGroup>

          {loadingMeta && <LoadingText>Cargando metadatos...</LoadingText>}

          {thumb && (
            <ThumbnailContainer>
              <Thumbnail src={thumb} alt="Miniatura del vídeo" />
            </ThumbnailContainer>
          )}

          <ButtonGroup>
            <Button type="button" onClick={onClose}>Cancelar</Button>
            <Button type="submit" $primary>Guardar vídeo</Button>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalContainer>
  );
};

export default VideoForm;